import type { Doc, DocMeta, DocPatch, User, Version } from "../shared/types";

export class Unauthorized extends Error {
  constructor() {
    super("unauthorized");
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });

  if (response.status === 401) throw new Unauthorized();
  if (!response.ok) {
    const detail = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((detail as { error?: string }).error ?? "request failed");
  }
  return (await response.json()) as T;
}

export const api = {
  me: () => call<{ user: User | null }>("/auth/me"),

  logout: () => call<{ ok: true }>("/auth/logout", { method: "POST" }),

  list: (filters: { kind?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.kind) params.set("kind", filters.kind);
    const query = params.toString();
    return call<{ documents: DocMeta[] }>(`/api/documents${query ? `?${query}` : ""}`);
  },

  search: (q: string, filters: { kind?: string } = {}) => {
    const params = new URLSearchParams({ q });
    if (filters.kind) params.set("kind", filters.kind);
    return call<{ documents: DocMeta[] }>(`/api/search?${params}`);
  },

  get: (key: string) => call<{ document: Doc }>(`/api/documents/${encodeURIComponent(key)}`),

  create: (patch: DocPatch) =>
    call<{ document: Doc }>("/api/documents", {
      method: "POST",
      body: JSON.stringify(patch),
    }),

  update: (id: string, patch: DocPatch) =>
    call<{ document: Doc }>(`/api/documents/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),

  remove: (id: string) =>
    call<{ ok: true }>(`/api/documents/${encodeURIComponent(id)}`, { method: "DELETE" }),

  versions: (id: string) =>
    call<{ versions: Version[] }>(`/api/documents/${encodeURIComponent(id)}/versions`),
};
